// Pure helpers for pieces that are DIVIDED among several players (sub-slots) and
// for choosing which other players a viewer watches draw live. No React or
// Firebase imports, so the geometry and the sampling can be unit-tested.

/**
 * Exact partition of one piece into `count` rectangles (fractions of the piece,
 * 0..1) and the rectangle for sub-slot `index`. Rows are balanced and the
 * longer rows come last: 1 -> whole, 2 -> two halves side by side, 3 -> one
 * full-width strip on top + two halves below, 4 -> 2x2, 5 -> 2+3, 7 -> 2+2+3 ...
 * The rectangles tile the piece with no gaps and no overlap.
 */
export function subSliceRect(index, count) {
  const m = Math.max(1, Math.floor(Number(count)) || 1);
  const i = Math.min(Math.max(0, Math.floor(Number(index)) || 0), m - 1);
  const rows = Math.max(1, Math.round(Math.sqrt(m)));
  const base = Math.floor(m / rows);
  const extra = m % rows; // the last `extra` rows hold one more cell

  let remaining = i;
  for (let row = 0; row < rows; row += 1) {
    const cells = base + (row >= rows - extra ? 1 : 0);
    if (remaining < cells) {
      return { x: remaining / cells, y: row / rows, w: 1 / cells, h: 1 / rows };
    }
    remaining -= cells;
  }
  return { x: 0, y: 0, w: 1, h: 1 };
}

/** All rectangles of a piece divided `count` ways (index order). */
export function subSliceRects(count) {
  const m = Math.max(1, Math.floor(Number(count)) || 1);
  return Array.from({ length: m }, (_, i) => subSliceRect(i, m));
}

/**
 * Whether a piece shared by several players is DIVIDED into one slice per player.
 *
 * OFF by default: when players outnumber pieces (e.g. 9 players, 4 pieces) the pieces stay WHOLE. Each player
 * is assigned a piece, draws all of it, and the live board shows every player's own drawing for that piece.
 * Nothing is cut again. The piece count is whatever the admin saved; players are just spread over it.
 *
 * It is decided HERE as well as on the server because slice values (sub_index / sub_count) are stored per
 * player: a session created before the change, or served by an older backend, still carries them, and
 * without this switch the app would keep cutting pieces into slivers (a thin strip to draw on, grey slice
 * boxes on the board). Set REACT_APP_DIVIDE_SHARED_PIECES=true (and DIVIDE_SHARED_PIECES=true on the
 * backend) to get the old divided behaviour back. Read at call time so tests can switch it.
 */
export function dividePiecesEnabled() {
  return process.env.REACT_APP_DIVIDE_SHARED_PIECES === 'true';
}

/** True when this player's piece is divided (sub_count > 1) AND division is switched on. */
export function isSliced(subCount) {
  return dividePiecesEnabled() && Math.floor(Number(subCount)) > 1;
}

/**
 * How many other players' live drawings each viewer subscribes to, by room size.
 * Every watched player is a continuous stream of preview images to that viewer, so
 * the total download grows as players x limit. 12 is fine for a classroom and
 * ruinous for 2,000 people: shrink it as the room grows (piece-mates always come
 * first inside pickLiveSample, so a small limit still shows who you share a piece with).
 */
export function liveWatchLimit(playerCount) {
  const n = Math.floor(Number(playerCount)) || 0;
  if (n < 100) return 12;
  if (n < 300) return 6;
  if (n < 800) return 3;
  return 2;
}

// FNV-1a 32-bit — tiny, deterministic, no dependencies.
export function hashString(text) {
  let h = 0x811c9dc5;
  const s = String(text);
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

// pickLiveSample runs on every Firebase event; re-hashing ~2,000 pairs each time
// is wasted work because hash(viewer|candidate) never changes. Bounded cache.
const PAIR_HASH_CACHE_MAX = 20000;
const pairHashCache = new Map();
function cachedPairHash(viewer, candidateId) {
  const key = `${viewer}|${candidateId}`;
  let h = pairHashCache.get(key);
  if (h === undefined) {
    if (pairHashCache.size >= PAIR_HASH_CACHE_MAX) pairHashCache.clear();
    h = hashString(key);
    pairHashCache.set(key, h);
  }
  return h;
}

/**
 * Which other players' live drawings this viewer watches. Players on the
 * viewer's own piece always come first; the rest are the `limit - mates` players
 * with the lowest hash(viewer|candidate) ("rendezvous" hashing). That makes the
 * choice:
 *  - different for every viewer (everyone sees different people drawing),
 *  - identical across refreshes (a pure function of the ids, no randomness),
 *  - stable when other players join or leave (only that player's slot changes).
 *
 * candidates: [{ key, userId, puzzle_id }] — `key` is the Firebase node key.
 * Returns an array of keys (never including the viewer).
 */
export function pickLiveSample(viewerId, ownPuzzleId, candidates, limit = 12) {
  const viewer = String(viewerId ?? '');
  const others = (Array.isArray(candidates) ? candidates : []).filter(
    (c) => c && String(c.userId) !== viewer
  );

  const mates = others
    .filter((c) => ownPuzzleId != null && Number(c.puzzle_id) === Number(ownPuzzleId))
    .sort((a, b) => String(a.userId).localeCompare(String(b.userId)));
  const rest = others
    .filter((c) => !mates.includes(c))
    .map((c) => ({ c, score: cachedPairHash(viewer, c.userId) }))
    .sort((a, b) => a.score - b.score || String(a.c.userId).localeCompare(String(b.c.userId)))
    .map(({ c }) => c);

  return [...mates, ...rest].slice(0, Math.max(0, limit)).map((c) => c.key);
}
